import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

const UNIT_OPTIONS = ["$", "AUD", "USD", "hrs", "posts", "campaigns", "videos", "%"];

const DEFAULT_BU_BUDGET = [
  { label: "Human Resources (this property)", unit: "hrs", amount: 0 },
  { label: "Hosting Cost", unit: "$", amount: 0 },
  { label: "Google Ads spend", unit: "$", amount: 0 },
  { label: "YouTube Ads spend", unit: "$", amount: 0 },
  { label: "Facebook boost budget", unit: "$", amount: 0 },
  { label: "Instagram boost budget", unit: "$", amount: 0 },
];

export default function BUBriefEditor({ businessUnitId, onClose }) {
  const [summary, setSummary] = useState("");
  const [budget, setBudget] = useState([]);
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState(null);
  const [defaultRate, setDefaultRate] = useState(0);

  useEffect(() => {
    if (!businessUnitId) return;
    Promise.all([
      api.get(`/business-units/${businessUnitId}/brief`).then((r) => r.data),
      api.get(`/app-settings`).then((r) => r.data).catch(() => ({ default_hourly_rate: 0 })),
    ]).then(([data, settings]) => {
      setMeta({ name: data.name, url: data.website_url });
      setSummary(data.brief_summary || "");
      const fallback = Number(settings?.default_hourly_rate) || 0;
      setDefaultRate(fallback);
      setBudget(
        data.budget_items?.length
          ? (() => {
              const rows = data.budget_items.map((r) => ({
                ...r,
                // Backfill hourly_rate on hrs rows that were saved before a
                // default rate existed.
                hourly_rate:
                  r.unit === "hrs" && !Number(r.hourly_rate)
                    ? fallback
                    : r.hourly_rate,
              }));
              // Every property budget carries a Hosting Cost line.
              if (!rows.some((r) => /hosting/i.test(r.label || ""))) {
                rows.splice(1, 0, { label: "Hosting Cost", unit: "$", amount: 0, hourly_rate: 0 });
              }
              return rows;
            })()
          : DEFAULT_BU_BUDGET.map((r) => ({
              ...r,
              hourly_rate: r.unit === "hrs" ? fallback : 0,
            }))
      );
    });
  }, [businessUnitId]);

  const updateRow = (i, key, val) =>
    setBudget(budget.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  const addRow = () => setBudget([
    ...budget,
    { item_id: `new_${Date.now()}_${budget.length}`, label: "", unit: "$", amount: 0, hourly_rate: defaultRate },
  ]);
  const removeRow = (i) => setBudget(budget.filter((_, idx) => idx !== i));

  const rowTotal = (r) => {
    if (r.unit !== "hrs") return null;
    const a = Number(r.amount) || 0;
    const hr = Number(r.hourly_rate) || 0;
    if (!hr) return null;
    return a * hr;
  };
  const grandHrCost = budget.reduce((s, r) => s + (rowTotal(r) || 0), 0);

  const save = async () => {
    setBusy(true);
    try {
      const cleaned = budget
        .filter((r) => r.label && r.label.trim())
        .map((r) => ({
          item_id: r.item_id,
          label: r.label.trim(),
          unit: r.unit || "$",
          amount: Number(r.amount) || 0,
          hourly_rate: r.unit === "hrs" ? Number(r.hourly_rate) || 0 : 0,
          notes: (r.notes || "").trim(),
        }));
      await api.patch(`/business-units/${businessUnitId}/brief`,
        { brief_summary: summary, budget_items: cleaned });
      toast.success("Property brief saved");
      if (onClose) onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  if (!meta) return null;

  return (
    <Card className="rounded-sm border-zinc-200 bg-zinc-50/60" data-testid="bu-brief-editor">
      <CardContent className="p-5 space-y-4">
        <div>
          <div className="label-overline">{meta.name}</div>
          <h3 className="font-display font-bold text-lg tracking-tight">Sub-brief &amp; budget</h3>
          <p className="text-xs text-zinc-500">Specific to <code className="font-mono">{meta.url}</code>. Rolls up into the client&apos;s combined view.</p>
        </div>

        <div className="space-y-2">
          <Label className="label-overline">Property-specific brief</Label>
          <Textarea rows={4} value={summary} onChange={(e) => setSummary(e.target.value)}
            placeholder="e.g. Hero product: passenger lifts. Target: Newcastle and Hunter Region. KPI: +50% organic clicks vs Q1 baseline."
            className="rounded-sm bg-white" data-testid="bu-brief-summary" />
        </div>

        <div className="space-y-2">
          <Label className="label-overline">Property-specific budget</Label>
          <p className="text-[11px] text-zinc-500 -mt-0.5">
            For rows in <code className="font-mono">hrs</code>, set the hourly rate to compute the
            total cost (e.g. <span className="font-mono">2 hrs × $175 = $350</span>).
          </p>
          <div className="grid grid-cols-[1fr_84px_92px_96px_104px_auto] gap-2 text-[10px] uppercase tracking-[0.18em] text-zinc-500 px-1 pb-1 border-b border-zinc-100">
            <div>Label</div>
            <div>Unit</div>
            <div className="text-right pr-2">Amount</div>
            <div className="text-right pr-2">Rate $/hr</div>
            <div className="text-right pr-2">Total $</div>
            <div></div>
          </div>
          {budget.map((row, i) => {
            const isHrs = row.unit === "hrs";
            const total = rowTotal(row);
            return (
              <div key={row.item_id || `idx-${i}`}
                className="grid grid-cols-[1fr_84px_92px_96px_104px_auto] gap-2 items-center"
                data-testid={`bu-budget-row-${i}`}>
                <Input value={row.label} onChange={(e) => updateRow(i, "label", e.target.value)}
                  className="rounded-sm h-9 text-sm bg-white" />
                <Select value={row.unit} onValueChange={(v) => updateRow(i, "unit", v)}>
                  <SelectTrigger className="rounded-sm h-9 text-sm bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {UNIT_OPTIONS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="number" step="0.01" value={row.amount}
                  onChange={(e) => updateRow(i, "amount", e.target.value)}
                  className="rounded-sm h-9 text-sm text-right font-mono bg-white"
                  data-testid={`bu-budget-amount-${i}`} />
                <Input
                  type="number" step="0.01" min="0"
                  value={isHrs ? (row.hourly_rate ?? "") : ""}
                  onChange={(e) => updateRow(i, "hourly_rate", e.target.value)}
                  disabled={!isHrs}
                  placeholder={isHrs ? "175" : "—"}
                  title={isHrs ? "Hourly rate in $" : "Only used when the unit is hrs"}
                  className={`rounded-sm h-9 text-sm text-right font-mono ${isHrs ? "bg-white" : "bg-zinc-100 text-zinc-400"}`}
                  data-testid={`bu-budget-rate-${i}`}
                />
                <div
                  className={`h-9 px-2 grid place-items-end text-sm font-mono rounded-sm border ${total != null ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-zinc-50 border-zinc-200 text-zinc-300"}`}
                  data-testid={`bu-budget-total-${i}`}
                  title={isHrs ? "amount × hourly_rate" : "Only used when the unit is hrs"}
                >
                  {total != null
                    ? `$${total.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                    : "—"}
                </div>
                <Button size="sm" variant="ghost" className="rounded-sm text-red-600 h-9 w-9 p-0"
                  onClick={() => removeRow(i)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            );
          })}
          <Button variant="outline" size="sm" onClick={addRow}
            className="rounded-sm w-full bg-white">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add line
          </Button>
          {grandHrCost > 0 && (
            <div className="flex items-center justify-end gap-3 pt-2 mt-1 border-t border-zinc-200 text-xs"
              data-testid="bu-budget-hr-total">
              <span className="label-overline">Total HR cost</span>
              <span className="font-display font-bold text-base text-emerald-900">
                ${grandHrCost.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 pt-2 border-t border-zinc-200">
          <Button onClick={save} disabled={busy}
            className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
            data-testid="bu-brief-save">
            <Save className="h-3.5 w-3.5 mr-1.5" /> Save
          </Button>
          {onClose && (
            <Button variant="ghost" className="rounded-sm" onClick={onClose}>Close</Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
